import { BulletList, AlertBox, Screenshot, ScreenshotGrid } from '../components/UIComponents';
import { Box, Laptop, Zap, Camera, ShieldCheck, Thermometer, Activity } from 'lucide-react';

export function DigitalTwin() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="digital-twin">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Box className="text-rose-400" size={22} />
                    8. Digital Twin
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    A live 3D view of your data center that shows your racks, devices, cameras, and power setup all in one place.
                </p>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-2.5">What Is a Digital Twin?</h3>
                    <BulletList items={[
                        'It is a virtual model of your physical data center that updates live with real data.',
                        'It combines your IT hardware—like racks, servers, and cables—with environmental numbers, such as temperature, humidity, and cooling system status.',
                        'It also brings your power chain (generators, UPS, PDUs) and live camera feeds directly into the 3D view.'
                    ]} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                        <h3 className="text-[15px] font-bold text-gray-100 mb-2.5 flex items-center gap-2">
                            <Activity size={16} className="text-rose-400" /> 8.1 Full 3D View & 8.2 Rack-Level Visualization
                        </h3>
                        <BulletList items={[
                            'Explore the data center in full 3D: orbit, pan, zoom, and walk around virtually.',
                            'Click on the building to see rooms, then down to racks, right down to individual installed servers.',
                            'View exactly where components sit in the rack, check power usage, and see their current health status.',
                            'Display temperature, humidity, and airflow directly over the racks.',
                            'See exactly where cameras connect to the switch, including PoE status and the physical cable path.',
                            'Watch live camera feeds inside the 3D view, complete with last-seen pictures and connection health.',
                            'Track temperature distribution, hot/cold aisles, cooling units, and overall airflow.',
                            'Map out your power delivery: watch the path from generators down to individual PDUs and track power events.',
                            'Important alerts appear right on the objects in 3D, and disappear when fixed.'
                        ]} />
                    </div>
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <h3 className="text-[15px] font-bold text-gray-100 mb-2.5 flex items-center gap-2">
                            <Thermometer size={16} className="text-blue-400" /> 8.5 Environmental Data & 8.6 Power Chain
                        </h3>
                        <p className="text-[13px] text-gray-200 leading-relaxed">
                            The Digital Twin doesn't just show devices; it pulls in data from thermometers, power monitors, and PDUs to give you the big picture of your facility's health.
                        </p>
                        <BulletList items={[
                            'Heat-maps instantly show where things are getting too hot or perfectly cooled.',
                            'Verify your hot/cold aisle setup is actually working with visual airflow and cooling markers.',
                            'Keep an eye on backup generator fuel, battery charge levels, running hours, and voltage.',
                            'Track if you are running on grid power or generator, how much runtime the UPS has left, and where power is flowing.'
                        ]} />
                        <AlertBox type="success" icon={<ShieldCheck size={16} />}>
                            Allows your team to inspect the facility remotely and securely, saving travel and time.
                        </AlertBox>
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[15px] font-bold text-gray-100 mb-2.5">8.7 Alerts, 8.8 Data Sources, and 8.9 Benefits</h3>
                    <BulletList items={[
                        'When something breaks, an alert is pinned directly onto that rack, switch, or camera in the 3D space.',
                        'The objects pulse or change colors based on how serious the issue is. Simply click them to dig into the details.',
                        'The map runs automatically off real data from SNMP, APIs, switch tables, and cameras—no manual drawing required.',
                        'Having a 3D view makes it far faster to figure out exactly where the problem is during an incident.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Data_Center_Digital_Twin.png"
                    alt="Data Center Digital Twin"
                />
                <Screenshot
                    src="/Data_Center_Digital_Twin1.png"
                    alt="Data Center Digital Twin"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function EndpointAgents() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="endpoint-agents">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Laptop className="text-sky-400" size={22} />
                    9. Endpoint Agents
                </h2>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">9.1 Supported Platforms</h3>
                    <div className="flex flex-wrap gap-2.5">
                        {['Windows', 'Linux (32/64-bit)', 'macOS'].map(os => (
                            <div key={os} className="px-3 py-1.5 bg-gray-900 border border-white/5 rounded-lg text-[13px] text-gray-200 font-medium">
                                {os}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">9.2 Data Collected by the Agent</h3>
                    <BulletList items={[
                        'Basic details: CPU, RAM, storage, architecture, BIOS info, and serial number.',
                        'Live usage: Processor load, memory usage, disk read/write speeds, and RAID health.',
                        'Process tracking: See what\'s running and set up alerts if specific processes take up too many resources.',
                        'Software tracking: Track installed programs, check software versions, and monitor missing security patches.',
                        'Security basics: Check if important services are running, review firewall settings, certificates, local user accounts, and critical logs.',
                        'Network details: Keep an eye on open ports, network interfaces, and traffic amounts per service.',
                        'Active Directory: For AD servers, see groups, policies, computers, and user statuses.',
                        'Docker: Check if containers are healthy, online, and see how much CPU/memory they consume.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">9.3 Active Directory & 9.4 Docker Monitoring</h3>
                    <BulletList items={[
                        'For machines running Active Directory, the agent pulls down user lists, group structures, computer names, and password policies so you can monitor AD health.',
                        'If the server is running Docker, the agent automatically spots containers and tracks their health, uptime, and versions without extra configuration.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">9.5 Remote SSH & Log Management</h3>
                    <BulletList items={[
                        'Connect directly to your agents via a secure, web-based SSH terminal right from your browser.',
                        'Execute commands and troubleshoot issues instantly without needing external SSH clients or VPNs.',
                        'Easily pull and download system logs directly from the agent interface for quick diagnostics and auditing.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Lightweight_Endpoint_Agents.png"
                    alt="Endpoint Agents"
                />
                <Screenshot
                    src="/Lightweight_Endpoint_Agents1.png"
                    alt="Endpoint Agents"
                />
                <Screenshot
                    src="/Endpoint_Agent_SSH_Terminal.png"
                    alt="Endpoint Agent SSH Terminal"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function OutOfBandMonitoring() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="oob-monitoring">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Zap className="text-yellow-400" size={22} />
                    10. Server Out-of-Band monitoring (iDRAC / iLO)
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <p className="text-[14px] text-gray-200 leading-relaxed mb-4">
                    Monitor your server hardware directly via management interfaces like iLO or iDRAC, even if the main operating system crashes or goes offline.
                </p>
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">10.1 Supported Hardware Platforms</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-black/40 rounded-lg border border-white/5">
                        <h4 className="text-[13px] font-bold text-yellow-400 mb-1">RAID Monitoring</h4>
                        <p className="text-[12px] text-gray-300">RAID level, physical disk count, logical volume status, individual disk health, degradation alerts, and predictive disk failure alerts.</p>
                    </div>
                    <div className="p-3 bg-black/40 rounded-lg border border-white/5">
                        <h4 className="text-[13px] font-bold text-yellow-400 mb-1">Health Metrics</h4>
                        <p className="text-[12px] text-gray-300">Check inside the chassis for HPE iLO (4/5) and Dell iDRAC (7-9): track temperatures, fan speeds, power supplies, memory health, disk SMART data, and view hardware logs.</p>
                    </div>
                </div>
                <p className="text-[12px] text-gray-300 mt-4">10.2 Immediately get warned if a RAID set starts falling apart or a disk shows signs it will fail soon.</p>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Server_Out-of-Band_monitoring.png"
                    alt="Server Out-of-Band Monitoring"
                />
                <Screenshot
                    src="/Server_Out-of-Band_monitoring1.png"
                    alt="Server Out-of-Band Monitoring"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function CameraMonitoring() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="camera-monitoring">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Camera className="text-red-400" size={22} />
                    11. IP Camera & Video Infrastructure
                </h2>
            </div>
            <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                <h3 className="text-[14px] font-bold text-gray-100 mb-3">11.1 Camera Monitoring Data</h3>
                <BulletList items={[
                    'Camera Health tracking: Automated ONVIF discovery and reachability checks.',
                    'Device Inventory: Includes your camera\'s model, firmware, IP, MAC, vendor, serial number, and user accounts.',
                    'Stream Health: Makes sure the video stream actually works and isn\'t blocked, rather than just pinging it.',
                    'Last Frame Grab: Captures and saves the most recent frames so you can see evidence of issues.',
                    'Live Monitoring: Provides a 2x2 live grid and lets you watch streams inside the 3D Digital Twin.',
                    'Storage Checks: Monitors your NVR and DVR for available space and disk health.',
                    'Offline Buffer: Stores camera data locally if the connection drops and syncs it back once connected.'
                ]} />
                <h3 className="text-[14px] font-bold text-gray-100 mt-5 mb-3">11.2 Last Frame Evidence & 11.3 Live Streaming</h3>
                <BulletList items={[
                    'The last-frame grab proves your camera is actively streaming, not just sitting on the network.',
                    'If the stream gets bad or the camera drops, it alerts you and saves the last good frame.',
                    'Watch live feeds in standard grids or right inside the Digital Twin interface linked to switch ports.',
                    'All streams use authenticated RTSP/ONVIF channels with health overlays and easy switching between groups.'
                ]} />
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/IP_Camera_&_Video_Infrastructure1.png"
                    alt="IP Camera & Video Infrastructure"
                />
                <Screenshot
                    src="/IP_Camera_&_Video_Infrastructure.png"
                    alt="IP Camera & Video Infrastructure"
                />
            </ScreenshotGrid>
        </section>
    );
}

export function KubernetesMonitoring() {
    return (
        <section className="doc-section scroll-mt-20 mt-12" id="kubernetes-monitoring">
            <div className="mb-3 pb-2 border-b border-white/10">
                <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                    <Activity className="text-cyan-400" size={22} />
                    11.5 Kubernetes Dashboard
                </h2>
                <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                    A comprehensive, real-time dashboard for monitoring your entire Kubernetes infrastructure, from cluster health down to individual pod performance.
                </p>
            </div>
            <div className="space-y-4">
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">11.5.1 Dashboard Tabs & Views</h3>
                    <BulletList items={[
                        'Pods Tab: View live pod statuses, manage containers, check resource limits, and track restart counts across all namespaces.',
                        'Events Tab: Monitor Kubernetes events, warnings, and errors in real-time to quickly identify and troubleshoot deployment issues.',
                        'History Tab: View historical performance metrics, trace lifecycle changes, and analyze the past states of your deployments.',
                        'Cluster Overview: Track overall cluster resource utilization including CPU, memory, and storage.'
                    ]} />
                </div>
                <div className="bg-gray-800 border border-white/5 rounded-xl p-5">
                    <h3 className="text-[14px] font-bold text-gray-100 mb-3">11.5.2 Interactive Shell & Logs</h3>
                    <BulletList items={[
                        'Interactive Web Shell: Open a secure, interactive web-based shell terminal directly into any running pod right from your browser.',
                        'Live Logs: Pull live container logs to view application output and debug issues instantly without needing external CLI tools.'
                    ]} />
                </div>
            </div>
            <ScreenshotGrid>
                <Screenshot
                    src="/Kubernetes_Pods_Tab.png"
                    alt="Kubernetes Pods Tab"
                />
                <Screenshot
                    src="/Kubernetes_Events_Tab.png"
                    alt="Kubernetes Events Tab"
                />
                <Screenshot
                    src="/Kubernetes_History_Tab.png"
                    alt="Kubernetes History Tab"
                />
                <Screenshot
                    src="/Kubernetes_Pod_Shell.png"
                    alt="Kubernetes Pod Interactive Shell"
                />
            </ScreenshotGrid>
        </section>
    );
}
